import { useId, useRef, useState, type ComponentType, type CSSProperties, type KeyboardEvent } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { SectionHeading } from "../SectionHeading";
import { PriorityDemo } from "../demo/PriorityDemo";
import { ComposeDemo } from "../demo/ComposeDemo";
import { MemoryDemo } from "../demo/MemoryDemo";
import { PromiseDemo } from "../demo/PromiseDemo";
import { BRAND } from "../brand";

type Chapter = {
  label: string;
  title: string;
  text: string;
  points: string[];
  Demo: ComponentType;
};

const CHAPTERS: Chapter[] = [
  {
    label: "Prioriser",
    title: "La bonne relance, au bon moment.",
    text: "Retrouvez les actions prévues aujourd’hui, les clients sans nouvelles et les retards les plus anciens. Les promesses en cours et les réponses à traiter ont leur propre place.",
    points: [
      "«\u00a0Échéances proches\u00a0»\u00a0: prévenez avant que la facture soit en retard",
      "Les sociétés d’un même groupe réunies par leur SIREN",
      "Triez par montant ou ancienneté, recherchez un client ou une facture",
    ],
    Demo: PriorityDemo,
  },
  {
    label: "Relancer",
    title: "Votre adresse. Vos mots. Votre relation.",
    text: `Choisissez un modèle, ajustez, relisez. Le message part de votre adresse Gmail, avec la signature de votre choix, et les factures en PDF sont jointes d’office. Rien dans l’email ne mentionne ${BRAND}.`,
    points: [
      "Six modèles fournis, tous modifiables",
      "Un seul message pour relancer tout un groupe",
      "Après une relance, le client revient dans la liste au bout de 3 jours (réglable)",
    ],
    Demo: ComposeDemo,
  },
  {
    label: "Garder le contexte",
    title: "Une facture change. L’historique reste.",
    text: "Appels, notes, promesses et réponses sont rangés sur le client, pas sur la facture. Un avoir, une refacturation, un relettrage dans Pennylane\u00a0: l’historique reste où il est.",
    points: [
      "Les réponses de vos clients arrivent dans leur fiche, prêtes à traiter",
      "Des contacts de relance distincts de ceux de la facturation",
      `Pennylane en lecture seule\u00a0: ${BRAND} n’y écrit rien`,
    ],
    Demo: MemoryDemo,
  },
  {
    label: "Suivre les promesses",
    title: "Une date promise devient une action à suivre.",
    text: "Notez la date promise au téléphone. Tant qu’elle court, le client sort de la liste\u00a0; si elle passe sans règlement, il y revient, avec la note de l’appel.",
    points: [
      "Mode session\u00a0: les clients s’enchaînent, le formulaire d’appel à côté",
      "Appels possibles depuis Aircall",
      "Statut «\u00a0Ne pas relancer\u00a0» pour les cas à part",
    ],
    Demo: PromiseDemo,
  },
];

function ChapterText({ chapter, index }: { chapter: Chapter; index: number }) {
  return (
    <>
      <p className="font-mono text-ui font-semibold text-action">
        {String(index + 1).padStart(2, "0")}
      </p>
      <h3 className="mt-3 font-display text-[clamp(1.6rem,2.6vw,2.2rem)] leading-[1.12] font-semibold tracking-[-0.012em] text-foreground">
        {chapter.title}
      </h3>
      <p className="mt-4 text-base leading-relaxed text-muted-foreground">
        {chapter.text}
      </p>
      <ul className="mt-5 space-y-2.5">
        {chapter.points.map((point, i) => (
          <li
            key={point}
            className="enc-tabpanel-point flex gap-2.5 text-sm text-foreground"
            style={{ "--i": i } as CSSProperties}
          >
            <Check aria-hidden className="mt-0.5 size-4 shrink-0 text-action" />
            {point}
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * Démonstrations locales : choix explicite et état conservé entre les étapes.
 * La pastille glisse vers l'onglet choisi ; après un premier choix, le
 * panneau affiché arrive (styles/motion.css, enc-tabpanel).
 */
export function HowItWorks() {
  const [active, setActive] = useState(0);
  const [touched, setTouched] = useState(false);
  const tabs = useRef<Array<HTMLButtonElement | null>>([]);
  const id = useId();
  const reduce = useReducedMotion();

  function select(index: number) {
    setActive(index);
    setTouched(true);
  }

  function onTabKey(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const next = event.key === "ArrowRight" ? (index + 1) % CHAPTERS.length
      : event.key === "ArrowLeft" ? (index + CHAPTERS.length - 1) % CHAPTERS.length
      : event.key === "Home" ? 0
      : event.key === "End" ? CHAPTERS.length - 1 : null;
    if (next === null) return;
    event.preventDefault();
    select(next);
    tabs.current[next]?.focus();
  }

  return (
    <section id="fonctionnement" className="py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="De la facture au règlement"
          title="Vos relances clients, enfin suivies."
          lead={`${BRAND} retrouve vos factures dans Pennylane. Vous choisissez qui relancer, les mots à employer et la prochaine action. Explorez le parcours ci-dessous.`}
        />
        <div role="tablist" aria-label="Étapes de la relance client" className="mt-8 grid grid-cols-2 gap-2 rounded-panel border border-border bg-surface p-2 sm:grid-cols-4">
          {CHAPTERS.map((chapter, index) => (
            <button
              key={chapter.label}
              ref={(element) => { tabs.current[index] = element; }}
              id={`${id}-tab-${index}`}
              role="tab"
              type="button"
              aria-selected={active === index}
              aria-controls={`${id}-panel-${index}`}
              tabIndex={active === index ? 0 : -1}
              onClick={() => select(index)}
              onKeyDown={(event) => onTabKey(event, index)}
              className={cn(
                "relative flex min-h-12 cursor-pointer items-center rounded-lg px-3 py-3 text-left text-ui font-semibold transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:px-4",
                // Le texte passe au blanc quand la pastille arrive, pas avant.
                active === index ? "text-primary-foreground delay-[50ms]" : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              {active === index && (
                <motion.span
                  data-tab-pill
                  layoutId={`${id}-pill`}
                  aria-hidden="true"
                  className="absolute inset-0 rounded-lg bg-primary"
                  transition={{ duration: reduce ? 0 : 0.28, ease: [0.22, 1, 0.36, 1] }}
                />
              )}
              <span className="relative inline-flex items-center gap-2 sm:gap-3">
                <span aria-hidden className="font-mono text-caption opacity-75">0{index + 1}</span>
                {chapter.label}
              </span>
            </button>
          ))}
        </div>
        {CHAPTERS.map((chapter, index) => (
          <div key={chapter.label} id={`${id}-panel-${index}`} role="tabpanel" aria-labelledby={`${id}-tab-${index}`} hidden={active !== index} data-animate={touched ? "true" : undefined} tabIndex={0} className="enc-tabpanel mt-8 rounded-panel focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring lg:mt-10">
            <div className="grid items-center gap-8 lg:min-h-[480px] lg:grid-cols-[0.8fr_1.2fr] lg:gap-14">
              <div className="enc-tabpanel-text"><ChapterText chapter={chapter} index={index} /></div>
              <div className="enc-tabpanel-demo min-w-0"><chapter.Demo /></div>
            </div>
          </div>
        ))}
        <p className="mt-5 text-caption text-subtle-foreground">Démonstration interactive · Données fictives · Aucun email envoyé</p>
      </div>
    </section>
  );
}
